import { FC, FormEvent } from 'react';
import { Divider, Button, Box, Typography } from '@mui/material';

import { CampaignPartner, CampaignPPC, CampaignStatus } from '@libs/types';
import { CampaignCreateDto } from '@libs/dto';
import { useForm, useDrawer } from '@carcareers/contexts';
import {
  Select,
  TextInput,
  DateRangeInput,
} from '@carcareers/components';
import { useCampaign } from '@carcareers/hooks';

interface CampaignCreateFormProps {
  onSuccess?: () => void;
  postId?: number;
}

export const CampaignCreateForm: FC<CampaignCreateFormProps> = ({ onSuccess = () => undefined, postId }) => {
  const { setOpen } = useDrawer();
  const { data, setErrors } = useForm<CampaignCreateDto>();
  const { createCampaign } = useCampaign();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (postId && data) {
      data.postId = postId;
    }

    const response = await createCampaign(data, { onSuccess });

    if (response?.data) {
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  const partnerOptions = Object.entries(CampaignPartner).map(([key, value]) => ({
    label: value,
    value: key,
  }));

  const ppcOptions = Object.entries(CampaignPPC).map(([key, value]) => ({
    label: value.toString(),
    value: key,
  }));

  const statusOptions = Object.entries(CampaignStatus).map(([key, value]) => ({
    label: value,
    value: key,
  }));

  return (
    <>
      <Typography variant="h6">Campaign Details</Typography>
      <Select name="partner" label="Partner" options={partnerOptions} />
      <Select name="ppc" label="Price Per Click" options={ppcOptions} />
      <TextInput name="budget" label="Budget" type="number" />
      <Select name="status" label="Status" options={statusOptions} />
      <Typography variant="h6">Campaign Duration</Typography>
      <DateRangeInput nameStart="startDate" nameEnd="endDate" />
      
      <Divider sx={{ flexGrow: 1 }} />
      
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" type="submit" onClick={handleSubmit}>
          Create Campaign
        </Button>
      </Box>
    </>
  );
};

export default CampaignCreateForm; 